import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { localModAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield, AlertTriangle, CheckCircle, Activity, Code, Download, Loader2, ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../components/ui/use-toast';

const LocalModDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [content, setContent] = useState('');
  const [language, setLanguage] = useState('en');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await localModAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleModerate = async () => {
    if (!content.trim()) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const response = await localModAPI.moderateContent(content, language);
      setResult(response.data);
      loadDashboard(); // Refresh stats
    } catch (error) {
      console.error('Moderation failed', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await localModAPI.exportCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `localmod_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Export réussi',
        description: 'Le fichier CSV a été téléchargé',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Échec de l\'export CSV',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="localmod-dashboard">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">LocalMod AI</h1>
            <p className="text-gray-600 mt-2">Multilingual content moderation powered by Claude Sonnet 4.5</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={handleExportCSV}
          disabled={exporting || !dashboard?.total_moderated}
          data-testid="export-csv-btn"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      {/* Stats Grid */}
      {!statsLoading && dashboard && (
        <div className="grid gap-6 md:grid-cols-4">
          <Card data-testid="total-moderated">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Moderated</CardTitle>
              <Activity className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {dashboard.total_moderated || 0}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="safe-content">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Safe Content</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {dashboard.safe_count || 0}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="flagged-content">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Flagged Content</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {dashboard.flagged_count || 0}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="api-calls">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">API Calls</CardTitle>
              <Code className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {dashboard.api_calls || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Moderation Tool */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Test Content Moderation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="content">Content to Moderate</Label>
              <Textarea
                id="content"
                placeholder="Enter text, review, or comment to moderate..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
                data-testid="moderate-content-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="language">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="it">Italiano</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={handleModerate} 
              disabled={loading || !content.trim()}
              className="w-full"
              data-testid="moderate-btn"
            >
              {loading ? 'Analyzing...' : 'Moderate Content'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Moderation Result</CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4" data-testid="moderation-result">
                <Alert className={result.is_safe ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                  <AlertDescription className="flex items-center space-x-2">
                    {result.is_safe ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="font-semibold text-green-900">Content is Safe</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <span className="font-semibold text-red-900">Content Flagged</span>
                      </>
                    )}
                  </AlertDescription>
                </Alert>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Confidence Score</p>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${result.is_safe ? 'bg-green-600' : 'bg-red-600'}`}
                      style={{ width: `${result.confidence_score}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{result.confidence_score}%</p>
                </div>

                {result.flags?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Flags Detected</p>
                    <div className="flex flex-wrap gap-2">
                      {result.flags.map((flag, idx) => (
                        <span key={idx} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {result.analysis && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Detailed Analysis</p>
                    <div className="space-y-2">
                      {Object.entries(result.analysis).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm capitalize text-gray-600">{key.replace('_', ' ')}</span>
                          <span className="text-sm font-semibold">{value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.explanation && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Explanation</p>
                    <p className="text-sm text-gray-600">{result.explanation}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Enter content to see moderation results</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Flag Breakdown */}
      {dashboard?.flag_breakdown && Object.keys(dashboard.flag_breakdown).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Flag Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {Object.entries(dashboard.flag_breakdown).map(([flag, count]) => (
                <div key={flag} className="flex justify-between items-center p-4 border rounded-lg">
                  <span className="capitalize text-gray-700">{flag}</span>
                  <span className="text-lg font-bold text-red-600">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LocalModDashboard;
